import classnames from 'classnames';
import { Box, Typography, Iconography } from '@lucid/lego';
import type { QuoteFileMetadata } from '../../../api/services/lupp/quotes/dto.quotes';
import styles from './DocumentsList.module.scss';

/**
 * Props for DocumentsList component
 */
type DocumentsListProps = {
  /** Array of uploaded document metadata */
  documents: QuoteFileMetadata[];
  /** Theme for the component */
  theme?: 'light' | 'dark';
  /** Test ID for the component */
  dataTestId?: string;
};

/**
 * Get icon name based on file MIME type
 * @param mimeType - MIME type of the file
 * @returns Icon name for the file type
 */
const getFileIcon = (mimeType: string): string => {
  if (mimeType.includes('pdf')) return 'file-pdf';
  if (mimeType.includes('image')) return 'image';
  return 'file';
};

/**
 * DocumentsList Component
 *
 * Displays a list of uploaded documents for a quote with file icons and checkmarks
 *
 * @component
 * @param {DocumentsListProps} props - Component props
 * @returns {JSX.Element} DocumentsList component
 *
 * @example
 * ```tsx
 * <DocumentsList
 *   documents={quoteFilesMetadata}
 *   theme="light"
 *   dataTestId="quote-documents-list"
 * />
 * ```
 */
export const DocumentsList = ({ documents, theme = 'light', dataTestId }: DocumentsListProps) => {
  if (!documents || documents.length === 0) {
    return null;
  }

  return (
    <Box className={classnames(styles.container, styles[theme])} data-testid={dataTestId}>
      <Box className={styles.header} mb={12}>
        <Typography variant="body" size="medium" color="gray-5" theme={theme}>
          Documents Available
        </Typography>
      </Box>
      <Box className={styles.documentsList}>
        {documents.map((doc, index) => (
          <Box
            key={doc.fileId || index}
            className={styles.documentItem}
            data-testid={`document-item-${index}`}
          >
            <Box className={styles.documentInfo}>
              <Iconography
                category="global"
                name={getFileIcon(doc.mimeType)}
                size={20}
                color="primary-1"
              />
              <Typography variant="body" size="small" color="gray-5" theme={theme}>
                {doc.fileNameOriginal}
              </Typography>
            </Box>
            <Iconography category="global" name="success" size={16} color="success-1" />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

DocumentsList.displayName = 'DocumentsList';

export default DocumentsList;
